import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Grid } from "@mui/material";
import classNames from "classnames";
import { Typography, styled } from "@mui/material";
import { FlightRemovalType, IBasicFlightType, IFlight, IFlightAircraft } from "@vmosolutions/oc-interfaces";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";
import FlightIcon from "@mui/icons-material/Flight";
import { useDrag } from "react-dnd";
import { ChipContextMenu } from "./ChipContextMenu";
import { v1 } from "uuid";
import { setHoveredEvent } from "src/redux/actions/flights/FlightTimeline.Actions";
import { useDispatch, useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/Warning";
import { IRootState } from "src/redux/reducers/CombinedReducers";

export interface IFlightChip {
  _id?: string;
  flightNumber: number;
  flightNumberSuffix?: string;
  origin: string;
  destination: string;
  flightType?: IBasicFlightType;
  aircraft?: IFlightAircraft;
  removalType?: FlightRemovalType;
}

export interface IFlightChipCollectionProps {
  flights: IFlight[];
  displayTotal?: number;
  disableFocus?: boolean;
  disableContext?: boolean;
}

interface IFlightChipProps {
  data?: IFlightChip;
  className?: string;
  onClickChip?: (flightId: string) => void;
  disableContext?: boolean;
  disableFocus?: boolean;
  disableDrag?: boolean;
  setParentAnchor?: React.Dispatch<React.SetStateAction<Element>>;
  showFlightNumberOnly?: boolean;
  /** Optional tooltip content shown on hover. Overrides the default cancelled-only tooltip. */
  tooltipContent?: React.ReactNode;
}

const OCFlightChip = styled(Grid)(({ theme }) => ({
  userSelect: "none",
  width: 120,
  height: 26,
  boxShadow: "2px 2px 5px " + theme.palette.common.black,
  justifyContent: "space-between",
  display: "flex",
  flexDirection: "row",
  margin: "2px",
  borderRadius: "2px",
  fontFamily: "B612 Mono, arial, sans-serif", // Cousine showed poor anti-aliasing (blurred)
  fontSize: 10,
  fontWeight: "normal",
  padding: "5px",
  alignItems: "center",
  color: theme.palette.primary.contrastText, // This get's overriden by the inline style applied to assign best contrast color.
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shortest,
  }),
  overflow: "hidden",
  position: "relative",
  "&.cancelled": {
    opacity: 0.75,
    userSelect: "none",
    "& .cancelled_icon": {
      marginLeft: "-5px",
      fontSize: 14, // Keep the warning icon within the chip height and clear of the flight info.
      flexShrink: 0, // Never let the flexing info squeeze the icon.
    },
  },
  "&.status_bar": {
    paddingTop: "2px",
    paddingBottom: "3px",
    boxShadow: "1px 1px 2px " + theme.palette.common.black,
    "& .origin, & .destination , & .flightnumber, & .event_title": {
      height: "auto",
      marginTop: "3px",
    },
  },
  "&.draggable": {
    cursor: "grab",
    "&:active": { cursor: "grabbing" },
  },
  "& div": {
    display: "flex",
    flexDirection: "row",
    padding: "0px 5px",
  },
  "& .details": {
    flexDirection: "column",
    "& div": { lineHeight: "9px", padding: 0 },
  },
  "& .aircraft_icon": {
    marginTop: "-2px",
    transform: "rotate(50deg) scale(1.4)",
    opacity: 0.2,
    "& path": { fill: "black" },
  },
  "& .info": {
    position: "absolute",
    width: 110,
    "& .flightnumber, & .event_title": {
      fontSize: 12,
      fontWeight: "bold",
      padding: "0px 2px",
    },
    "& .origin, & .destination ": {
      padding: "0px 2px",
    },
  },
  "&.interactive": {
    "&:hover": { margin: "2px 0px", width: 124, cursor: "pointer" },
    "&:active": { margin: "2px 4px", width: 116 },
  },
}));

export const FlightChip = (props: IFlightChipProps): JSX.Element => {
  const {
    data,
    className,
    onClickChip,
    disableContext,
    disableFocus,
    disableDrag,
    setParentAnchor,
    showFlightNumberOnly = false,
    tooltipContent,
  } = props;
  const dispatch = useDispatch();
  const [contextAnchor, setContextAnchor] = useState(null);
  const isICAO = useSelector((state: IRootState) => state.authentication.appSettings.blockInfo.icao);
  //!! Watch out because only a limited number of airports are stored in the redux store.
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const [originCode, setOriginCode] = useState(data?.origin);
  const [destCode, setDestCode] = useState(data?.destination);
  // Event has ID and isn't temp.
  const assignedEvent: boolean = data?._id && !data?._id.includes("temp");

  useEffect(() => {
    if (isICAO) {
      setOriginCode(data?.origin);
      setDestCode(data?.destination);
    } else {
      setOriginCode(airportOptions[data?.origin]?.iata ?? data?.origin);
      setDestCode(airportOptions[data?.destination]?.iata ?? data?.destination);
    }
  }, [airportOptions, data?.destination, data?.origin, isICAO]);

  const flightRemovalType: FlightRemovalType | null = useMemo(() => {
    return data?.removalType;
  }, [data?.removalType]);

  //is there a better way to turn off the drag?
  const dragType = (flightRemovalType ?? data?.aircraft) ? "FLIGHT" : "UNASSIGNED_FLIGHT";
  const [collected, drag, dragPreview] = useDrag(
    {
      type: dragType,
      item: { flight: data, type: dragType },
    },
    [dragType, data]
  );

  // Set context menu anchor (opens it);
  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      if (disableContext || !assignedEvent || flightRemovalType === FlightRemovalType.DeleteFlight) {
        return;
      }
      event.preventDefault();
      event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
      setContextAnchor(event.currentTarget);
    },
    [disableContext, assignedEvent, flightRemovalType]
  );

  // Fire off onClickChip if supplied by properties in valid event and not disabled
  const handleClickChip = useCallback(
    (event: React.MouseEvent): void => {
      if (assignedEvent && onClickChip && typeof onClickChip === "function" && !disableFocus && !flightRemovalType) {
        event.stopPropagation(); // important when used in the table where rows are clickable.
        onClickChip(data?._id);
      }
    },
    [data?._id, assignedEvent, onClickChip, disableFocus, flightRemovalType]
  );

  if (data) {
    const { _id, flightNumber, flightNumberSuffix, origin, destination, flightType, aircraft } = data;
    const flightId = _id ? _id : v1();

    if ((!flightNumber && flightNumber !== 0) || !origin || !destination || !flightType) {
      return null;
    }
    const color = flightType?.color;
    // If all required data is passed, render the flight chip.
    const chipClass = flightId + "-flightChip";
    // Cancelled/deleted chips also render a warning icon. Let the info flex to fill the
    // space the icon leaves rather than reserving a fixed width (which clipped the destination).
    const cancelledFull = !!flightRemovalType && !showFlightNumberOnly;
    const frColor = color ? (assignedEvent ? invertHexColor(color) : `#${color}`) : "blue";
    const bgColor = color ? (assignedEvent ? `#${color}` : invertHexColor(color)) : "white";
    const borderStyle = color ? (assignedEvent ? "none" : `1px dashed #${color}`) : "none";

    return (
      <Tooltip
        disableFocusListener={true}
        disableTouchListener={true}
        disableHoverListener={!flightRemovalType && !tooltipContent} // Enable if cancelled OR custom tooltip supplied.
        title={
          tooltipContent
            ? tooltipContent
            : flightRemovalType
              ? `Flight has been ${flightRemovalType === FlightRemovalType.CancelFlight ? "canceled" : "deleted"}`
              : ""
        }
        placement="top"
      >
        <OCFlightChip
          ref={!disableDrag && drag && assignedEvent && !flightRemovalType ? drag : null}
          item
          id={flightId}
          key={flightId}
          sx={{
            color: `${frColor} !important`,
            backgroundColor: `${bgColor} !important`,
            border: `${borderStyle} !important`,
            boxShadow: !assignedEvent && "none",
          }}
          // Apply interactive components only if id is passed in.
          className={classNames(
            className,
            "flightchip",
            !!onClickChip && assignedEvent && "interactive",
            chipClass,
            flightRemovalType && "cancelled"
          )}
          onMouseEnter={() => {
            if (assignedEvent && !flightRemovalType) dispatch(setHoveredEvent(data as IFlight));
          }}
          onMouseLeave={() => {
            if (assignedEvent && !flightRemovalType) dispatch(setHoveredEvent(null));
          }}
          onClick={handleClickChip}
          onContextMenu={handlePopoverOpen}
        >
          <>
            {flightRemovalType ? (
              <WarningIcon color="error" className="cancelled_icon" />
            ) : (
              aircraft && <FlightIcon className="aircraft_icon" />
            )}
            <Grid
              container
              className="info"
              wrap="nowrap"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                flex: cancelledFull ? 1 : "none",
                px: cancelledFull ? 0 : undefined,
                marginLeft: cancelledFull ? 0.5 : 0,
                width: cancelledFull ? "auto !important" : `${showFlightNumberOnly ? 70 : 110}px !important`,
                position: cancelledFull ? "static" : !showFlightNumberOnly ? "absolute" : "static",
              }}
            >
              <>
                {!showFlightNumberOnly && (
                  <Grid item className="origin">
                    {origin}
                  </Grid>
                )}
                <Grid item className="flightnumber">
                  {flightNumber + (flightNumberSuffix ? flightNumberSuffix : "")}
                </Grid>
                {!showFlightNumberOnly && (
                  <Grid item className="destination">
                    {!showFlightNumberOnly && destination}
                  </Grid>
                )}
              </>
            </Grid>
            {!disableContext && assignedEvent && (
              <ChipContextMenu
                event={data as IFlight}
                anchorEl={contextAnchor}
                setAnchor={setContextAnchor}
                setParentAnchor={setParentAnchor}
              />
            )}
          </>
        </OCFlightChip>
      </Tooltip>
    );
  }
  return null;
};

/**
 * Shared component that can be used to display a specific count of selected flight chips.
 * @param props -
 */
export const FlightChipCollection = (props: IFlightChipCollectionProps): JSX.Element => {
  const { flights, displayTotal, disableFocus, disableContext } = props;
  const totalShown = displayTotal ?? 20;
  return (
    <Grid container direction={"row"} justifyContent={"center"}>
      {flights.map((selectedFlight: IFlight, index: number) => (
        <Grid item key={index}>
          {index < totalShown && (
            <FlightChip data={selectedFlight} disableContext={disableContext} disableFocus={disableFocus} />
          )}
          {flights.length >= totalShown && index === totalShown - 1 && (
            <Typography
              variant="body2"
              color="textPrimary"
              style={{ margin: 5, textAlign: "right" }}
            >{`...${flights.length} total flights`}</Typography>
          )}
        </Grid>
      ))}
    </Grid>
  );
};
